import React, { useState } from 'react';
import useAdminReviewList from './useAdminReviewList';
import { getInvitedCreatorVerifications, approveCreator, rejectCreator } from '../../services/api';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../auth/AuthContext';
import {
  CheckCircle, XCircle, UserPlus, Mail, Instagram, Globe, Search,
  X, Star, Clock, Link2
} from 'lucide-react';
import AdminPageHeader from './AdminPageHeader';
import './AdminStyles.scss';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.2, 0.8, 0.2, 1] } }
};

const StatusBadge = ({ status }) => {
  const config = {
    pending:   { color: '#64748b', bg: 'rgba(100, 116, 139, 0.12)', label: 'Sin postulación a red curada' },
    submitted: { color: '#ffc107', bg: 'rgba(255, 193, 7, 0.12)',  label: 'Postulación en revisión' },
    approved:  { color: '#10b981', bg: 'rgba(16, 185, 129, 0.12)', label: 'Miembro de Red Curada' },
    rejected:  { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.12)',  label: 'Postulación denegada' },
  };
  const c = config[status] || config.pending;
  return (
    <span style={{
      padding: '4px 12px', borderRadius: '20px', fontSize: '0.75rem',
      fontWeight: 600, background: c.bg, color: c.color, border: `1px solid ${c.color}25`
    }}>
      {c.label}
    </span>
  );
};

const InvitedCreators = () => {
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [processing, setProcessing] = useState(false);
  const [success, setSuccess] = useState(null);
  const { currentUser } = useAuth();
  const canReview = Boolean(currentUser?.is_superuser);

  const { results: creators, loading, error: loadError, page, setPage, pagination, summary, refresh } = useAdminReviewList(getInvitedCreatorVerifications, { search: searchTerm, status: filterStatus });
  const filteredCreators = creators;

  const handleApprove = async (creatorId) => {
    setProcessing(true);
    try {
      await approveCreator(creatorId);
      refresh();
      setSuccess('Creador aprobado; ahora tiene acceso completo.');
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError('Error al aprobar');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async (e) => {
    e.preventDefault();
    setProcessing(true);
    try {
      await rejectCreator(selectedCreator.id, { reason: rejectionReason });
      refresh();
      setShowRejectModal(false);
      setRejectionReason('');
      setSuccess('Solicitud rechazada.');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Error al rechazar');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) return <div className="dashboard-loading"><div className="spinner" /></div>;

  const submittedCount = summary.submitted || 0;
  const approvedCount = summary.approved || 0;

  return (
    <motion.div className="dashboard-view__inner" variants={containerVariants} initial="hidden" animate="visible">
      <AdminPageHeader
        title="Creadores Invitados"
        subtitle="Monitoriza a los creadores invitados por negocios clientes. Los invitados operan en su propio entorno privado sin requerir aprobación del admin. La aprobación administrativa solo aplica cuando el creador postula a la red curada de FOTH."
        breadcrumbs="Invitados"
      />

      <div className="dashboard-view__grid">
        <div className="dashboard-view__metric-grid">
          <motion.div className="dashboard-view__metric-card" variants={itemVariants}>
            <div className="dashboard-view__metric-label">TOTAL INVITADOS</div>
            <div className="dashboard-view__metric-row">
              <span className="dashboard-view__metric-value">{summary.total || 0}</span>
            </div>
            <div className="dashboard-view__metric-content" style={{ marginTop: '12px' }}>
              <div className="dashboard-view__metric-icon-wrap dashboard-view__metric-icon-wrap--info">
                <UserPlus className="dashboard-view__metric-icon" />
              </div>
            </div>
          </motion.div>
          <motion.div className="dashboard-view__metric-card" variants={itemVariants}>
            <div className="dashboard-view__metric-label">POSTULACIONES EN REVISIÓN</div>
            <div className="dashboard-view__metric-row">
              <span className="dashboard-view__metric-value">{submittedCount}</span>
            </div>
            <div className="dashboard-view__metric-content" style={{ marginTop: '12px' }}>
              <div className="dashboard-view__metric-icon-wrap dashboard-view__metric-icon-wrap--gold">
                <Clock className="dashboard-view__metric-icon" />
              </div>
            </div>
          </motion.div>
          <motion.div className="dashboard-view__metric-card" variants={itemVariants}>
            <div className="dashboard-view__metric-label">APROBADOS</div>
            <div className="dashboard-view__metric-row">
              <span className="dashboard-view__metric-value">{approvedCount}</span>
            </div>
            <div className="dashboard-view__metric-content" style={{ marginTop: '12px' }}>
              <div className="dashboard-view__metric-icon-wrap dashboard-view__metric-icon-wrap--success">
                <CheckCircle className="dashboard-view__metric-icon" />
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      <div className="admin-pagination"><span>{pagination.count} creadores</span><div><button type="button" className="admin-btn admin-btn--outline admin-btn--sm" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>Anterior</button><span>Página {page} de {pagination.pages || 1}</span><button type="button" className="admin-btn admin-btn--outline admin-btn--sm" disabled={page >= (pagination.pages || 1)} onClick={() => setPage(value => value + 1)}>Siguiente</button></div></div>

      <div className="dashboard-view__panel">
        <div className="luxury-filters">
          <div className="search-box-modern">
            <Search size={18} />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              className="admin-input-minimal"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-actions">
            <select
              className="admin-select-minimal"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value)}
            >
              <option value="">Todos los estados</option>
              <option value="pending">Sin postulación (Solo invitados)</option>
              <option value="submitted">Postulaciones en revisión</option>
              <option value="approved">Aprobados en red curada</option>
              <option value="rejected">Postulaciones rechazadas</option>
            </select>
          </div>
        </div>

        <AnimatePresence>
                {success && <motion.div className="alert-luxury alert-luxury--success" role="status" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>{success}</motion.div>}
                {(error || loadError) && <motion.div className="alert-luxury alert-luxury--danger" role="alert" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}>{error || loadError}</motion.div>}
        </AnimatePresence>

        <div className="verifications-stack">
          {filteredCreators.length === 0 ? (
            <div className="empty-state-luxury">
              <UserPlus size={40} opacity={0.2} />
              <h3>No hay creadores invitados</h3>
              <p>Cuando un negocio invite creadores a una colaboración, aparecerán aquí.</p>
            </div>
          ) : (
            filteredCreators.map(creator => (
              <motion.div key={creator.id} className="verification-item-lux" variants={itemVariants}>
                <div className="item-header">
                  <div className="biz-info">
                    <div className="avatar-box" style={{ background: '#3b82f6' }}>
                      <Star size={20} />
                    </div>
                    <div className="text-box">
                      <h4>
                        {creator.user?.username}
                        <span style={{
                          marginLeft: '8px', padding: '2px 8px', fontSize: '0.65rem',
                          background: 'rgba(59,130,246,0.15)', color: '#60a5fa',
                          borderRadius: '12px', fontWeight: 700, letterSpacing: '0.05em'
                        }}>
                          INVITADO
                        </span>
                      </h4>
                      <span>{new Date(creator.user?.date_joined).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <StatusBadge status={creator.verification_status} />
                </div>

                <div className="details-grid">
                  <div className="detail-col">
                    <label>Contacto</label>
                    <p><Mail size={12} /> {creator.user?.email}</p>
                    <p>
                      <Instagram size={12} />
                      {creator.instagram_account_connected
                        ? <span style={{ color: '#10b981' }}> Conectado</span>
                        : <span style={{ color: '#ffc107' }}> No conectado</span>
                      }
                    </p>
                  </div>
                  <div className="detail-col">
                    <label>Colaboración Invitada</label>
                    {creator.invited_collab_info ? (
                      <>
                        <p><Link2 size={12} /> {creator.invited_collab_info.title}</p>
                        <p><Globe size={12} /> Negocio: {creator.invited_collab_info.business_username || 'N/A'}</p>
                        <p style={{ fontSize: '0.75rem', color: '#888' }}>
                          ID: {creator.invited_collab_info.id?.substring(0, 8)}...
                        </p>
                      </>
                    ) : (
                      <p style={{ color: '#ef4444' }}>
                        <XCircle size={12} /> Collab eliminada o no asignada
                      </p>
                    )}
                  </div>
                </div>

                {creator.verification_status === 'submitted' ? (
                  canReview && (
                    <div className="action-row" style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-start', marginTop: '12px' }}>
                      <p style={{ margin: 0, fontSize: '0.78rem', color: '#ffc107', fontWeight: 600 }}>
                        Este creador completó el onboarding y solicitó acceso completo a la red curada de FOTH:
                      </p>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          className="admin-btn admin-btn--primary"
                          onClick={() => handleApprove(creator.id)}
                          disabled={processing}
                        >
                          <CheckCircle size={16} /> Aprobar para Red Curada
                        </button>
                        <button
                          className="admin-btn admin-btn--outline"
                          style={{ color: '#ff4d5e', borderColor: 'rgba(255,77,94,0.2)' }}
                          onClick={() => { setSelectedCreator(creator); setShowRejectModal(true); }}
                        >
                          <XCircle size={16} /> Denegar Red Curada
                        </button>
                      </div>
                    </div>
                  )
                ) : creator.verification_status === 'approved' ? (
                  <div style={{ padding: '8px 12px', background: 'rgba(16, 185, 129, 0.08)', borderRadius: '10px', fontSize: '0.78rem', color: '#10b981', marginTop: '12px' }}>
                    <CheckCircle size={13} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                    Miembro activo de la red curada de FOTH con acceso completo.
                  </div>
                ) : creator.verification_status === 'rejected' ? (
                  <div style={{ padding: '8px 12px', background: 'rgba(239, 68, 68, 0.08)', borderRadius: '10px', fontSize: '0.78rem', color: '#ef4444', marginTop: '12px' }}>
                    Postulación a red curada denegada. El creador continúa activo para sus colaboraciones privadas invitadas.
                  </div>
                ) : (
                  <div style={{ padding: '8px 12px', background: 'rgba(100, 116, 139, 0.08)', borderRadius: '10px', fontSize: '0.78rem', color: '#888', marginTop: '12px' }}>
                    Creador invitado activo. No requiere aprobación de administrador para colaborar en su collab invitada. Aún no ha postulado a la red curada.
                  </div>
                )}
              </motion.div>
            ))
          )}
        </div>
      </div>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <div className="modal-luxury-overlay" role="presentation" onClick={() => setShowRejectModal(false)}>
            <motion.div
              className="modal-luxury-content"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              role="dialog" aria-modal="true" aria-labelledby="invited-reject-title" onClick={e => e.stopPropagation()}
            >
              <div className="modal-luxury-header">
                <h3 id="invited-reject-title">Denegar Solicitud</h3>
                <button type="button" aria-label="Cerrar" className="close-btn-circle" onClick={() => setShowRejectModal(false)}><X size={18} /></button>
              </div>
              <form onSubmit={handleReject}>
                <div className="modal-luxury-body">
                  <p>Razón del rechazo para <strong>{selectedCreator?.user?.username}</strong>:</p>
                  <textarea
                    className="admin-textarea"
                    style={{ width: '100%', minHeight: '100px', borderRadius: '12px', padding: '12px', border: '1px solid #d8dee8', marginTop: '12px' }}
                    placeholder="Explica la razón del rechazo..."
                    value={rejectionReason}
                    maxLength={2000}
                    onChange={e => setRejectionReason(e.target.value)}
                    required
                  />
                </div>
                <div className="modal-luxury-footer">
                  <button className="admin-btn admin-btn--outline" type="button" onClick={() => setShowRejectModal(false)}>Cancelar</button>
                  <button className="admin-btn" style={{ background: '#ff4d5e' }} type="submit" disabled={processing}>Confirmar Denegación</button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default InvitedCreators;
